import { FocusMonitor } from "@angular/cdk/a11y";
import { Directionality } from "@angular/cdk/bidi";
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  Optional,
  Output,
  ViewChild,
  forwardRef,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { NzCheckboxComponent } from "ng-zorro-antd/checkbox";
import { NzSafeAny } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { UiCheckboxWrapperComponent } from "@webapp/ui/checkbox/components/checkbox-wrapper/checkbox-wrapper.component";

type UiCheckboxStatus = "default" | "error";

@Component({
  selector: "ui-checkbox,[ui-checkbox]",
  exportAs: "uiCheckbox",
  templateUrl: "checkbox.component.html",
  styleUrls: ["checkbox.component.less"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiCheckboxComponent),
      multi: true,
    },
  ],
})
export class UiCheckboxComponent extends NzCheckboxComponent implements AfterContentInit {
  @Input("uiValue") public nzValue: NzSafeAny | null = null;
  @Input("uiAutoFocus") @InputBoolean() public nzAutoFocus = false;
  @Input("uiIndeterminate") @InputBoolean() public nzIndeterminate = false;
  @Input("uiChecked") @InputBoolean() public nzChecked = false;
  @Input("uiId") public nzId: string | null = null;
  @Input() @InputBoolean() public uiTriState = false;
  @Input() public uiErrorMessage = "";
  @Input() public uiStatus: UiCheckboxStatus = "default";
  /** Aria label of the checkbox. */
  @Input() public a11yLabel: string;
  /** Input that can be used to specify the `aria-labelledby` attribute. */
  @Input() public a11yLabelledby: string;
  @ViewChild("checkboxLabel", { static: true }) private checkboxLabel!: ElementRef<HTMLElement>;

  @HostBinding("class.ant-checkbox-disabled")
  @Input("uiDisabled")
  @InputBoolean()
  public nzDisabled = false;

  @Output("uiCheckedChange") public readonly nzCheckedChange = new EventEmitter<boolean>();
  @Output() public readonly uiIndeterminateChange = new EventEmitter<boolean>();

  @HostBinding("class.ui-status-error") get uiStatusError(): boolean {
    return this.uiStatus === "error";
  }

  constructor(
    ngZone: NgZone,
    elementRef: ElementRef<HTMLElement>,
    @Optional() private uiCheckboxWrapperComponent: UiCheckboxWrapperComponent,
    cdr: ChangeDetectorRef,
    private uiFocusMonitor: FocusMonitor,
    @Optional() directionality: Directionality
  ) {
    super(ngZone, elementRef, uiCheckboxWrapperComponent, cdr, uiFocusMonitor, directionality);
  }

  public ngAfterContentInit(): void {
    this.ensureA11yLabel();
  }

  public innerCheckedChange(checked: boolean): void {
    // This handles the default checkbox behavior
    if (!this.uiTriState) {
      super.innerCheckedChange(checked);
      return;
    }

    // This handles the tri-state checkbox behavior
    if (!this.nzDisabled) {
      if (this.nzChecked && !checked) {
        this.nzChecked = false;
        this.nzIndeterminate = true;
      } else {
        this.nzChecked = !this.nzIndeterminate;
        this.nzIndeterminate = false;
      }
      this.uiIndeterminateChange.emit(this.nzIndeterminate);
      this.onChange(this.nzChecked);
      this.nzCheckedChange.emit(this.nzChecked);
      if (this.uiCheckboxWrapperComponent) {
        this.uiCheckboxWrapperComponent.onChange();
      }
    }
  }

  public focus(): void {
    this.uiFocusMonitor.focusVia(this.inputElement, "mouse");
  }

  private ensureA11yLabel(): void {
    if (!this.a11yLabel && this.checkboxLabel.nativeElement?.textContent) {
      this.a11yLabel = this.checkboxLabel.nativeElement.textContent;
    }
  }
}
